import { useMemo, useState, useRef, useEffect } from "react";
import Icon from "../Icon";
import { getTipoColor, getTipoIcon, esNoLaborable, today, getTipoProd, prodBand } from "../../utils";
import { resumenSubtareas } from "../../subtareas";
import {
  tramosDelDia, sinHorario, repartirEnCarriles, marcarSolapes, huecos,
  rangoHorario, marcasHorarias, pctDe, fmtHora, resumenDia, diasConActividad,
  nivelDetalle, primeraPalabra, inicialDe, ANCHO_MIN_BARRA,
  segmentosDeTramo, filasDia, ALTO_FILA, ALTO_FILA_SUB,
} from "./cronologiaDia";
import { clicable } from "../ClicableTeclado.jsx";

// La columna de nombres, como en la cronologia de proyectos. Es la que hace que
// el nombre de una tarea se lea siempre entero: dentro de la barra dependia del
// ancho, y una tarea de diez minutos no tiene ancho ninguno.
const ANCHO_NOMBRES = 210;

// Lo que mide la regla de horas. La cabecera de la columna de nombres calza
// justo esto para que la primera fila empiece a la misma altura a los dos lados.
const ALTO_REGLA = 22;

// Aire a los lados del eje. La primera y la ultima marca van centradas sobre su
// hora, asi que la mitad del rotulo cae fuera del rango: sin este margen "08:00"
// y "13:00" salian partidos contra el borde de la tarjeta. Tiene que dar para
// media etiqueta de cinco caracteres.
const MARGEN_EJE = 22;

// El dia con su nombre delante: en una cronologia importa saber que un lunes
// esta partido en seis trozos, no solo que es el dia 14.
const fmtDia = iso => {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d)
    .toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long" });
};

const fmtDur = min => {
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  if (!h) return m + "m";
  return m ? h + "h " + m + "m" : h + "h";
};

// El tooltip de una tarea. Sale en su fila de la izquierda y en su barra: son
// dos sitios donde se pulsa lo mismo, y tienen que contar lo mismo.
const tituloTarea = (t, subs) => {
  const a = t.entrada;
  const pesoBanda = prodBand(t.prod);
  return `${fmtHora(t.ini)}–${fmtHora(t.fin)} · ${fmtDur(t.fin - t.ini)}`
    + `\n${a.type || "Sin tipo"}${a.description ? "\n" + a.description : ""}`
    + `${t.solapaCon ? `\nSe solapa con ${t.solapaCon} tarea${t.solapaCon > 1 ? "s" : ""}` : ""}`
    + `${subs.n ? `\n${subs.hechas}/${subs.n} subtareas hechas` : ""}`
    + `\nProductividad ${pesoBanda.label.toLowerCase()} · ${t.prod}%`;
};

const tituloSub = (seg, parcial) =>
  `${seg.sub.texto || "Sin descripción"}\n${seg.sub.hecha ? "Hecha" : "Pendiente"}`
  + `${seg.estimada ? " · sin estimar, repartida por igual" : ` · ${fmtDur(seg.fin - seg.ini)}`}`
  + `${parcial ? "\nEsta tarea continúa en otro día" : ""}`;

// ── La columna de la izquierda ───────────────────────────────────────────────

// El nombre de una tarea, con su formato: el color y el icono de su tipo, la
// descripcion entera y debajo el horario. Es la fila que se lee cuando la barra
// no cabe.
function NombreTarea({ f, col, icon, activa, abierta, onAbrir, onOpen }) {
  const { t, tiene } = f;
  const a = t.entrada;
  const subs = resumenSubtareas(a.subtareas);
  const titulo = a.description || a.type || "Sin descripción";

  return (
    <div className={"cr-row" + (activa ? " cr-on" : "")}
      style={{ height: f.alto, gap: 6 }}
      onClick={() => onOpen?.(a)}
      title={tituloTarea(t, subs)}>

      {tiene ? (
        <button aria-label={abierta ? "Plegar las subtareas" : "Ver como se reparte el tiempo"} className="btn" title={abierta ? "Plegar las subtareas" : "Ver como se reparte el tiempo"}
          onClick={e => { e.stopPropagation(); onAbrir?.(a); }}
          style={{ background: "none", border: "none", padding: 0, lineHeight: 0,
            cursor: "pointer", flexShrink: 0, width: 11 }}>
          <Icon name={abierta ? "chevron-down" : "chevron-right"} size={9} color="var(--text3)" />
        </button>
      ) : <span style={{ width: 11, flexShrink: 0 }} />}

      {icon
        ? <Icon name={icon} size={11} color={col} style={{ flexShrink: 0 }} />
        : <span style={{ width: 8, height: 8, borderRadius: 2, background: col, flexShrink: 0 }} />}

      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1, overflow: "hidden" }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, overflow: "hidden",
          textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text)" }}>
          {titulo}
        </span>
        <span style={{ fontSize: 9.5, color: "var(--text3)", whiteSpace: "nowrap",
          overflow: "hidden", textOverflow: "ellipsis" }}>
          {fmtHora(t.ini)}–{fmtHora(t.fin)} · {fmtDur(t.fin - t.ini)}
          {subs.n ? ` · ${subs.hechas}/${subs.n}` : ""}
        </span>
      </span>
    </div>
  );
}

// El nombre de una subtarea, colgando de la de su tarea. Sangrada y con la
// misma casilla que lleva en el Registro: hecha o pendiente.
function NombreSub({ f, col, onOpen }) {
  const { seg, t } = f;
  const parcial = !!(t.cortaAntes || t.cortaDespues);
  return (
    <div className="cr-row" style={{ height: f.alto, gap: 6, paddingLeft: 27 }}
      onClick={() => onOpen?.(t.entrada)}
      title={tituloSub(seg, parcial)}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
        background: seg.sub.hecha ? col : "transparent", border: "1.5px solid " + col }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 10.5, overflow: "hidden",
        textOverflow: "ellipsis", whiteSpace: "nowrap",
        color: seg.sub.hecha ? "var(--text2)" : "var(--text)",
        textDecoration: seg.sub.hecha ? "line-through" : "none" }}>
        {seg.sub.texto || "Sin descripción"}
      </span>
    </div>
  );
}

// ── Las barras ───────────────────────────────────────────────────────────────

// La barra de una tarea. La posicion va en % del rango del dia —asi el eje sigue
// al panel sin recalcular nada— pero lo que se escribe DENTRO se decide con el
// ancho real en pixeles, porque un 3% puede ser un icono o un parrafo.
//
// El texto de la barra es ahora una REPETICION del nombre de la izquierda, no la
// unica forma de saber que tarea es: si no cabe, no pasa nada.
export function Barra({ t, col, icon, activa, anchoPx, top, alto, onOpen }) {
  const izq = pctDe(t.ini, t.desde, t.hasta);
  const der = pctDe(t.fin, t.desde, t.hasta);
  const ancho = Math.max(der - izq, 0);
  const a = t.entrada;

  const px = Math.max((ancho / 100) * anchoPx, ANCHO_MIN_BARRA);
  const nivel = nivelDetalle(px);
  const titulo = a.description || a.type || "Sin descripción";
  // Sin icono configurado en el tipo, la inicial: una barra de 20 px tiene que
  // decir algo, y una mancha de color no lo dice.
  const inicial = icon ? "" : inicialDe(a.type, a.description);
  const subs = resumenSubtareas(a.subtareas);
  const etiqueta = nivel === "palabra" ? primeraPalabra(titulo) : titulo;

  return (
    <div className="cr-bar"
      onClick={() => onOpen?.(a)}
      title={tituloTarea(t, subs)}
      style={{
        position: "absolute", left: izq + "%", width: ancho + "%", minWidth: ANCHO_MIN_BARRA,
        top, height: alto, transition: "box-shadow .12s",
        background: col, color: "#fff", borderRadius: 6,
        borderTopLeftRadius:  t.cortaAntes   ? 0 : 6,
        borderBottomLeftRadius: t.cortaAntes ? 0 : 6,
        borderTopRightRadius:  t.cortaDespues ? 0 : 6,
        borderBottomRightRadius: t.cortaDespues ? 0 : 6,
        boxShadow: activa ? "0 0 0 2px var(--surface), 0 0 0 4px " + col : "0 1px 3px rgba(0,0,0,.16)",
        display: "flex", alignItems: "center", justifyContent: nivel === "marca" ? "center" : "flex-start",
        gap: 4, padding: nivel === "marca" ? 0 : "0 6px", overflow: "hidden",
        cursor: "pointer", boxSizing: "border-box",
      }}>
      {/* El borde abierto de una tarea que viene de ayer o sigue mañana */}
      {t.cortaAntes && <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "rgba(255,255,255,.75)" }} />}
      {t.cortaDespues && <span style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 3, background: "rgba(255,255,255,.75)" }} />}

      {icon
        ? <Icon name={icon} size={11} color="#fff" style={{ opacity: .95, flexShrink: 0 }} />
        : inicial && (
          <span style={{ fontSize: 11, fontWeight: 800, opacity: .95, flexShrink: 0, lineHeight: 1 }}>{inicial}</span>
        )}

      {nivel !== "marca" && (
        <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
          {etiqueta}
        </span>
      )}

      {nivel === "completo" && (
        <span style={{ marginLeft: "auto", paddingLeft: 6, fontSize: 10, fontWeight: 700, opacity: .85, whiteSpace: "nowrap", flexShrink: 0 }}>
          {fmtHora(t.ini)}–{fmtHora(t.fin)}
        </span>
      )}
    </div>
  );
}

// La barra de una subtarea, en su propia fila bajo la de su tarea.
//
// Se situa con pctDe sobre el MISMO eje que las barras de arriba, asi que queda
// alineada con la de su tarea por construccion, sin pasar pixeles.
export function BarraSub({ f, col, top, alto, anchoPx, desde, hasta, onOpen }) {
  const { seg, t } = f;
  const izq = pctDe(seg.ini, desde, hasta);
  const ancho = Math.max(pctDe(seg.fin, desde, hasta) - izq, 0);
  const px = (ancho / 100) * anchoPx;
  const parcial = !!(t.cortaAntes || t.cortaDespues);

  return (
    <div {...clicable(() => onOpen?.(t.entrada))}
      title={tituloSub(seg, parcial)}
      style={{
        position: "absolute", left: izq + "%", width: ancho + "%",
        top, height: alto, borderRadius: 4, boxSizing: "border-box", cursor: "pointer",
        display: "flex", alignItems: "center", padding: px > 30 ? "0 5px" : 0,
        overflow: "hidden", opacity: parcial ? .65 : 1,
        // Hecha en color pleno, pendiente traslucida: es la misma lectura que la
        // casilla del Registro, sin repetir el texto.
        background: seg.sub.hecha ? col : col + "38",
        border: seg.sub.hecha ? "none" : `1px solid ${col}77`,
        borderStyle: seg.estimada ? "dashed" : "solid",
        color: seg.sub.hecha ? "#fff" : "var(--text2)",
      }}>
      {px > 30 && (
        <span style={{ fontSize: 9.5, fontWeight: 700, whiteSpace: "nowrap",
          overflow: "hidden", textOverflow: "ellipsis" }}>
          {px > 70 ? seg.sub.texto : primeraPalabra(seg.sub.texto)}
        </span>
      )}
    </div>
  );
}

// Lo que la tarea duro y sus subtareas no reparten, sobre la propia fila de la
// tarea. El hueco es el dato, no un fallo: estirar los segmentos para taparlo lo
// esconderia. Y si suman de mas, se dice: se han escalado para caber.
function MarcasReparto({ t, top, alto, desde, hasta }) {
  const { segmentos, resto, desborde, suma, dur } = segmentosDeTramo(t);
  if (!segmentos.length) return null;
  const finUltimo = segmentos[segmentos.length - 1].fin;

  return (
    <>
      {resto > 0 && (
        <div title={`${fmtDur(resto)} de la tarea sin repartir en subtareas`}
          style={{
            position: "absolute", left: pctDe(finUltimo, desde, hasta) + "%",
            width: Math.max(pctDe(t.fin, desde, hasta) - pctDe(finUltimo, desde, hasta), 0) + "%",
            top, height: alto, borderRadius: 4, pointerEvents: "none",
            background: "repeating-linear-gradient(135deg,rgba(255,255,255,.55),rgba(255,255,255,.55) 4px,transparent 4px,transparent 8px)",
            boxSizing: "border-box",
          }} />
      )}

      {desborde && (
        <div title={`Las subtareas suman ${fmtDur(suma)} y la tarea dura ${fmtDur(dur)}`}
          style={{
            position: "absolute", left: pctDe(t.ini, desde, hasta) + "%",
            width: Math.max(pctDe(t.fin, desde, hasta) - pctDe(t.ini, desde, hasta), 0) + "%",
            top: top - 2, height: alto + 4, borderRadius: 6, pointerEvents: "none",
            border: "1px dashed #d97706", boxSizing: "border-box",
          }} />
      )}
    </>
  );
}

// ── El bloque de un dia ──────────────────────────────────────────────────────

// Nombres a la izquierda, regla de horas arriba y una fila por tarea: la misma
// forma que la cronologia de proyectos, pero midiendo minutos de una jornada en
// vez de dias de un proyecto.
function BloqueDia({
  iso, entradas, agendaTipos, agendaTiposData, festivos, editId, onOpen, verHuecos,
  subtareasAbiertas = [],
}) {
  const { tramos, carriles, sueltas, desde, hasta, marcas, libres, resumen } = useMemo(() => {
    const brutos = tramosDelDia(entradas, iso, a => getTipoProd(a.type, agendaTiposData));
    const conSolapes = marcarSolapes(brutos);
    const { desde, hasta } = rangoHorario(conSolapes);
    return {
      tramos: conSolapes.map(t => ({ ...t, desde, hasta })),
      // Ya no coloca nada: solo cuenta cuantas llegaron a ir a la vez, que es lo
      // que dice la cabecera.
      carriles: repartirEnCarriles(conSolapes).carriles,
      sueltas: sinHorario(entradas, iso),
      desde, hasta,
      marcas: marcasHorarias(desde, hasta),
      libres: huecos(conSolapes, desde, hasta, 20),
      resumen: resumenDia(conSolapes),
    };
  }, [entradas, iso, agendaTiposData]); // eslint-disable-line

  const esHoy = iso === today();
  const noLaborable = esNoLaborable(iso, festivos);
  // La aguja del ahora solo tiene sentido si hoy cae dentro del rango pintado.
  const ahora = (() => {
    if (!esHoy) return null;
    const d = new Date();
    const m = d.getHours() * 60 + d.getMinutes();
    return m >= desde && m <= hasta ? m : null;
  })();

  // Que tareas estan desplegadas. Vive aqui y no en la fila porque el `y` de
  // TODAS las de abajo depende de ello, y no en CronologiaDiaView porque cada
  // dia se despliega por su cuenta. La prop solo da el valor inicial: es lo que
  // hace el desglose comprobable en las pruebas de render.
  const [abiertas, setAbiertas] = useState(() => new Set(subtareasAbiertas.map(String)));
  const alternar = a => setAbiertas(prev => {
    const n = new Set(prev);
    const k = String(a.id);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  // En su propio useMemo: desplegar no tiene por que recalcular solapes, rango
  // horario ni marcas del eje.
  const { filas, alto } = useMemo(() => filasDia(tramos, abiertas), [tramos, abiertas]);

  // El ancho real de la zona de barras. Las barras lo necesitan para decidir si
  // les cabe el titulo, la primera palabra o solo el icono; el 700 de partida es
  // lo que mide en un portatil y solo se usa hasta que el navegador mide.
  //
  // Solo se lee el ANCHO. Desplegar una tarea cambia la ALTURA del contenedor, y
  // leer height aqui meteria a la vista en una realimentacion sin fin.
  const pista = useRef(null);
  const [anchoPx, setAnchoPx] = useState(700);
  useEffect(() => {
    const el = pista.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => {
      const w = e?.contentRect?.width;
      if (w) setAnchoPx(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const colorDe = a => getTipoColor(a.type, agendaTipos, agendaTiposData);

  return (
    <div style={{ background: "var(--surface)", borderRadius: 12, border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)", overflow: "hidden" }}>

      {/* Cabecera del dia */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 14px", borderBottom: "1px solid var(--border)", background: noLaborable ? "#fefce8" : "var(--surface2)" }}>
        <Icon name={esHoy ? "clock-history" : "calendar3"} size={13} color={esHoy ? "var(--accent)" : "var(--text3)"} />
        <span style={{ fontWeight: 800, fontSize: 12.5, color: esHoy ? "var(--accent)" : "var(--text)" }}>
          {fmtDia(iso)}
        </span>
        {esHoy && <span style={{ fontSize: 9.5, fontWeight: 800, padding: "2px 7px", borderRadius: 20, background: "var(--accent)", color: "var(--on-accent)" }}>HOY</span>}
        {noLaborable && (
          <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: "#fef3c7", color: "var(--warn-text)" }}>
            {festivos?.[iso] || "Fin de semana"}
          </span>
        )}
        <span style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center", fontSize: 10.5, color: "var(--text2)", flexWrap: "wrap" }}>
          {resumen.primera !== null && (
            <span><b style={{ color: "var(--text)" }}>{fmtHora(resumen.primera)}</b> → <b style={{ color: "var(--text)" }}>{fmtHora(resumen.ultima)}</b></span>
          )}
          <span title="Minutos con al menos una tarea encima; lo que se solapa cuenta una sola vez">
            Ocupado <b style={{ color: "var(--text)" }}>{fmtDur(resumen.minutosOcupados)}</b>
          </span>
          {resumen.solapadas > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontWeight: 700, color: "#b45309" }}>
              <Icon name="exclamation-triangle-fill" size={10} color="#b45309" />
              {resumen.solapadas} solapadas · {carriles} en paralelo
            </span>
          )}
        </span>
      </div>

      <div style={{ padding: "0 0 12px" }}>
        {tramos.length === 0 ? (
          <div style={{ fontSize: 11.5, color: "var(--text3)", padding: "12px 14px" }}>
            Ninguna entrada de este día tiene hora de inicio y de fin.
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "flex-start" }}>

            {/* Columna de nombres */}
            <div className="cr-names" style={{ width: ANCHO_NOMBRES, flexShrink: 0 }}>
              <div style={{ height: ALTO_REGLA, display: "flex", alignItems: "flex-end",
                padding: "0 10px 4px", fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: ".07em", color: "var(--text3)", boxSizing: "border-box",
                borderBottom: "1px solid var(--border)" }}>
                Tarea
              </div>
              {filas.map(f => f.clase === "tarea" ? (
                <NombreTarea key={f.clave} f={f}
                  col={colorDe(f.t.entrada)}
                  icon={getTipoIcon(f.t.entrada.type, agendaTiposData)}
                  activa={String(editId) === String(f.t.entrada.id)}
                  abierta={f.abierta}
                  onAbrir={alternar}
                  onOpen={onOpen} />
              ) : (
                <NombreSub key={f.clave} f={f} col={colorDe(f.t.entrada)} onOpen={onOpen} />
              ))}
            </div>

            {/* Regla de horas y barras */}
            <div style={{ flex: 1, minWidth: 0, padding: `0 ${MARGEN_EJE}px` }}>
              {/* El borde va DENTRO del alto: la cabecera de la columna de
                  nombres tambien lo mete, y un pixel de diferencia descuadra
                  cada fila con su barra. */}
              <div style={{ position: "relative", height: ALTO_REGLA, boxSizing: "border-box",
                borderBottom: "1px solid var(--border)" }}>
                {marcas.map(m => (
                  <span key={m.min} style={{
                    position: "absolute", left: pctDe(m.min, desde, hasta) + "%", transform: "translateX(-50%)",
                    top: 4, fontSize: 9.5, fontWeight: 700,
                    color: m.rotulada ? "var(--text3)" : "transparent", whiteSpace: "nowrap",
                  }}>{fmtHora(m.min)}</span>
                ))}
              </div>

              <div ref={pista} style={{ position: "relative", height: alto }}>
                {/* Rejilla horaria */}
                {marcas.map(m => (
                  <span key={m.min} style={{
                    position: "absolute", left: pctDe(m.min, desde, hasta) + "%", top: 0, bottom: 0, width: 1,
                    background: "var(--border2)", opacity: m.rotulada ? .9 : .5,
                  }} />
                ))}

                {/* Huecos sin nada encima: el dia tambien se lee por lo que falta */}
                {verHuecos && libres.map(h => (
                  <span key={h.ini} title={`Sin tareas · ${fmtDur(h.fin - h.ini)}`} style={{
                    position: "absolute", left: pctDe(h.ini, desde, hasta) + "%",
                    width: Math.max(pctDe(h.fin, desde, hasta) - pctDe(h.ini, desde, hasta), 0) + "%",
                    top: 0, bottom: 0,
                    background: "repeating-linear-gradient(135deg,var(--bg),var(--bg) 5px,transparent 5px,transparent 10px)",
                  }} />
                ))}

                {/* El renglon de cada fila, para que la barra y su nombre se
                    sigan con la vista a lo ancho del panel. */}
                {filas.map(f => (
                  <span key={"r" + f.clave} style={{
                    position: "absolute", left: 0, right: 0, top: f.y + f.alto - 1, height: 1,
                    background: "var(--border2)", opacity: .7,
                  }} />
                ))}

                {filas.map(f => f.clase === "tarea" ? (
                  <div key={"b" + f.clave}>
                    <Barra t={f.t}
                      col={colorDe(f.t.entrada)}
                      icon={getTipoIcon(f.t.entrada.type, agendaTiposData)}
                      activa={String(editId) === String(f.t.entrada.id)}
                      anchoPx={anchoPx}
                      top={f.y + 6} alto={f.alto - 13}
                      onOpen={onOpen} />
                    {f.abierta && (
                      <MarcasReparto t={f.t} top={f.y + 6} alto={f.alto - 13}
                        desde={desde} hasta={hasta} />
                    )}
                  </div>
                ) : (
                  <BarraSub key={"b" + f.clave} f={f}
                    col={colorDe(f.t.entrada)}
                    top={f.y + 4} alto={f.alto - 9}
                    anchoPx={anchoPx} desde={desde} hasta={hasta}
                    onOpen={onOpen} />
                ))}

                {/* La aguja del ahora, con su pildora rotulada: es la misma marca
                    que lleva la cronologia de proyectos, y ahi se sabe de un
                    golpe que esa linea es hoy y no un borde de la rejilla. */}
                {ahora !== null && (
                  <span title={"Ahora · " + fmtHora(ahora)} style={{
                    position: "absolute", left: pctDe(ahora, desde, hasta) + "%", top: -4, bottom: 0,
                    width: 2, background: "var(--red)", borderRadius: 1, pointerEvents: "none",
                  }}>
                    <span style={{
                      position: "absolute", left: "50%", top: -13, transform: "translateX(-50%)",
                      fontSize: 8.5, fontWeight: 800, letterSpacing: ".04em", lineHeight: 1,
                      padding: "2px 6px", borderRadius: 7, background: "var(--danger)", color: "var(--on-danger)",
                    }}>AHORA</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {sueltas.length > 0 && (
          <div style={{ margin: "10px 14px 0", paddingTop: 9, borderTop: "1px dashed var(--border)" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
              Sin horario · {sueltas.length}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sueltas.map(a => {
                const col = colorDe(a);
                return (
                  <span key={a.id} {...clicable(() => onOpen?.(a))}
                    title="Ponle hora de inicio y de fin para verla en la cronología"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer",
                      fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 20,
                      background: col + "15", color: col, border: "1px dashed " + col + "80",
                      maxWidth: 260, overflow: "hidden",
                    }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.description || a.type || "Sin descripción"}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// La cronologia del cuaderno: un bloque por dia, el mas reciente arriba.
export default function CronologiaDiaView({
  entradas = [], agendaTipos = [], agendaTiposData = [], festivos = {},
  editId = null, onOpen, subtareasAbiertas = [], desplegarSubtareas = true,
}) {
  const [verHuecos, setVerHuecos] = useState(true);

  // Las entradas que tienen desglose. Si no hay ninguna, el interruptor de
  // desplegar sobra: no se ofrece un mando que no hace nada.
  const conSubtareas = useMemo(
    () => entradas.filter(a => Array.isArray(a.subtareas) && a.subtareas.length).map(a => String(a.id)),
    [entradas]);
  // Desplegadas de entrada: el desglose es el detalle que se venia a ver, y
  // pedirlo tarea por tarea lo escondia. La prop existe para poder pintar la
  // vista plegada en las pruebas, donde no hay quien desmarque la casilla.
  const [todasAbiertas, setTodasAbiertas] = useState(desplegarSubtareas);

  const dias = useMemo(() => diasConActividad(entradas), [entradas]);

  const conHorario = useMemo(
    () => dias.reduce((s, d) => s + tramosDelDia(entradas, d).length, 0),
    [dias, entradas]);

  if (!dias.length) {
    return (
      <div style={{ textAlign: "center", padding: "50px 20px", color: "var(--text3)" }}>
        <Icon name="clock-history" size={32} color="#d1d5db" style={{ display: "block", margin: "0 auto 12px" }} />
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Sin entradas para este filtro</div>
        <div style={{ fontSize: 12 }}>Cambia el período para ver la cronología</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 11, color: "var(--text2)" }}>
        <span>
          <b style={{ color: "var(--text)" }}>{dias.length}</b> día{dias.length > 1 ? "s" : ""} ·{" "}
          <b style={{ color: "var(--text)" }}>{conHorario}</b> tarea{conHorario === 1 ? "" : "s"} con horario
        </span>
        <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={verHuecos} onChange={e => setVerHuecos(e.target.checked)} />
          Marcar los huecos sin tareas
        </label>
        {conSubtareas.length > 0 && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={todasAbiertas} onChange={e => setTodasAbiertas(e.target.checked)} />
            Desplegar las subtareas
          </label>
        )}
      </div>

      {/* Aviso cuando el filtro trae entradas pero ninguna tiene horas: sin esto
          la vista parece rota en vez de vacia a proposito. */}
      {conHorario === 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 11px", borderRadius: 9, background: "var(--warn-bg)", border: "1px solid var(--warn-line)", fontSize: 11.5, color: "var(--warn-text)" }}>
          <Icon name="exclamation-triangle-fill" size={12} color="#a16207" />
          Ninguna entrada del período tiene hora de inicio y de fin. Ponlas en el Registro y aparecerán aquí.
        </div>
      )}

      {dias.map(iso => (
        // La `key` lleva el interruptor a propósito: cambiarlo remonta el bloque
        // para que el nuevo conjunto inicial entre en vigor. Eso descarta lo que
        // hubiera desplegado a mano, que es justo lo que significa "desplegar (o
        // plegar) todas".
        <BloqueDia key={iso + (todasAbiertas ? ":sub" : "")}
          subtareasAbiertas={todasAbiertas ? conSubtareas : subtareasAbiertas}
          iso={iso}
          entradas={entradas}
          agendaTipos={agendaTipos}
          agendaTiposData={agendaTiposData}
          festivos={festivos}
          editId={editId}
          onOpen={onOpen}
          verHuecos={verHuecos} />
      ))}
    </div>
  );
}
